import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ESTRUCTURA_PDF, groupColor, useShowHitoCode, type HitoCatalog, type HitoCatalogItem } from "../lib/hitos";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";

/** Ficha de un hito (feedback de Carlos, 20 oct 2026): qué es, de qué fuentes sale y
 *  desde cuándo lo vigila HelixScan. Cada fuente empezó en una fecha distinta (la API de
 *  CIMA el 10 de julio; las fichas técnicas el 28 de septiembre; el Nomenclátor de
 *  facturación el 13 de octubre): lo anterior a esa fecha es historia, no evento. */
let catalogPromise: Promise<HitoCatalog> | null = null;
function loadCatalog(): Promise<HitoCatalog> {
  if (!catalogPromise) catalogPromise = api.get<HitoCatalog>("/hitos").catch(e => { catalogPromise = null; throw e; });
  return catalogPromise;
}

export default function HitoSheet({ hitoId, onClose }: { hitoId: string; onClose: () => void }) {
  const t = useT();
  const fmt = useFmt();
  const showCode = useShowHitoCode();
  const [h, setH] = useState<HitoCatalogItem | null>(null);
  useEffect(() => { loadCatalog().then(c => setH(c.items.find(i => i.id === hitoId) ?? null)).catch(() => {}); }, [hitoId]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const d = (iso: string | null | undefined) => iso ? fmt.date(new Date(iso)) : "—";
  const row = (label: string, value: React.ReactNode) => value ? (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 10, padding: "7px 0", borderTop: "1px solid var(--clr-border2)" }}>
      <span style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</span>
      <span style={{ fontSize: "0.84rem", lineHeight: 1.45 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div role="dialog" aria-modal="true" aria-label={t("hitos.sheetTitle")} onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(11,18,21,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "var(--clr-modal-solid)", border: "1px solid var(--clr-border)", borderRadius: 22, padding: "1.4rem 1.5rem", width: 640, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)", color: "var(--clr-text)" }}>
        {!h ? <div style={{ color: "var(--clr-text4)" }}>…</div> : (<>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.7rem", fontWeight: 700, color: groupColor(h.grupo), textTransform: "uppercase", letterSpacing: 0.8 }}>
                {t(`hitos.group${h.grupo}`)}{showCode ? ` · ${h.id}` : ""}
              </div>
              <h3 style={{ margin: "4px 0 0", fontSize: "1.05rem", fontWeight: 700 }}>{h.nombre}</h3>
            </div>
            <button onClick={onClose} aria-label={t("hitos.sheetClose")}
              style={{ background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999, width: 32, height: 32, cursor: "pointer", color: "var(--clr-text3)", flexShrink: 0 }}>✕</button>
          </div>
          {row(t("hitos.sheetDescription"), h.descripcion)}
          {row(t("hitos.sheetCatalogSources"), h.fuentes)}
          {row(t("hitos.sheetMonitoredSince"), h.cubierto === false
            ? t(h.activo === false ? "hitos.inactivo" : "hitos.sinCobertura")
            : (h.vigilado_desde ? <strong>{d(h.vigilado_desde)}</strong> : "—"))}
          {row(t("hitos.sheetHelixSources"), h.fuentes_helixscan?.length ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {h.fuentes_helixscan.map(f => (
                <li key={f.code}>{f.name} <span style={{ color: "var(--clr-text3)" }}>· {t("hitos.sheetSince", { date: d(f.desde) })}</span></li>
              ))}
            </ul>
          ) : null)}
          {row(t("hitos.sheetCritical"), (h.sev_propio || h.sev_competencia) ? [
            h.sev_propio ? t("hitos.sheetCriticalOwn", { v: t(h.critico_propio ? "hitos.yes" : "hitos.no") }) : null,
            h.sev_competencia ? t("hitos.sheetCriticalComp", { v: t(h.critico_competencia ? "hitos.yes" : "hitos.no") }) : null,
          ].filter(Boolean).join(" · ") : null)}
          {row(t("hitos.sheetFrequency"), h.frecuencia)}
          {row(t("hitos.sheetWindow"), h.ventana)}
          <p style={{ margin: "10px 0 0", fontSize: "0.72rem", color: "var(--clr-text4)", lineHeight: 1.45 }}>{t("hitos.sheetHistoryNote")}</p>
          <p style={{ margin: "8px 0 0", fontSize: "0.78rem" }}><a href={ESTRUCTURA_PDF} target="_blank" rel="noreferrer" style={{ color: "var(--clr-org-primary)", fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>{t("hitos.structurePdfFull")} ↗</a></p>
        </>)}
      </div>
    </div>
  );
}
